import { ArrowLeft, Check, FileText, ListChecks } from "lucide-react";
import { FaqSection } from "@/components/FaqSection";
import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import { content, whatsappUrl } from "@/lib/content";
import type { VendorOperator } from "@/lib/vendors";
import { isoServicePath } from "@/lib/vendors";
import { ArticleSidebar, EditorialImage } from "@/components/insights/ArticleEditorial";

const labels = {
  eyebrow: { en: "Oil & Gas Vendor Registration", ar: "تسجيل موردي النفط والغاز" },
  back: { en: "Oil & Gas Registration", ar: "تسجيل النفط والغاز" },
  howTitle: { en: "How registration works", ar: "كيف يتم التسجيل" },
  docsTitle: { en: "Documents typically required", ar: "الوثائق المطلوبة عادةً" },
  isoTitle: {
    en: "ISO guidance for your activity",
    ar: "إرشادات الأيزو بحسب نشاطك",
  },
  platformTitle: { en: "The platform", ar: "المنصة" },
  factsTitle: { en: "Key facts", ar: "أهم الحقائق" },
  sourcesTitle: { en: "Sources", ar: "المصادر" },
};

export function VendorRegistrationPage({ operator }: { operator: VendorOperator }) {
  const { t, dir } = useLanguage();
  const editorial = Boolean(operator.image);
  const enquiryLabel = t({
    en: "Request a registration quotation",
    ar: "اطلب عرض سعر لخدمة التسجيل",
  });
  const enquiryUrl = `${whatsappUrl}?text=${encodeURIComponent(
    t({
      en: `Hello IraqCert, I would like a quotation for registration support with ${t(operator.name)}.\nCompany name:\nCompany activity:\nCountry of incorporation:\nBuyer request and current stage (optional):\nTarget date (if any):\nPage: /services/oil-gas-registration/${operator.slug}`,
      ar: `مرحباً عراق سيرت، أرغب في عرض سعر لخدمة التسجيل لدى ${t(operator.name)}.\nاسم الشركة:\nنشاط الشركة:\nبلد تأسيس الشركة:\nطلب الجهة المشترية ومرحلة الطلب الحالية (اختياري):\nالموعد المستهدف (إن وجد):\nالصفحة: /ar/services/oil-gas-registration/${operator.slug}`,
    }),
  )}`;

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#f5f3ef] border-b border-border">
        <div
          className={`relative mx-auto w-full px-5 lg:px-8 ${editorial ? "max-w-[1280px] py-14 sm:py-20" : "max-w-[1280px] py-14 sm:py-20"}`}
        >
          <LangLink
            to="/services/oil-gas-registration"
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-background"
          >
            <ArrowLeft className={`size-4 ${dir === "rtl" ? "-scale-x-100" : ""}`} />
            {t(labels.back)}
          </LangLink>
          <div
            className={
              editorial
                ? "mt-6 grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,440px)] lg:gap-16"
                : ""
            }
          >
            <div className="mt-6 max-w-2xl text-start">
              <span className="inline-block rounded-full bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                {t(labels.eyebrow)}
              </span>
              <h1 className="mt-6 text-4xl font-extrabold leading-tight text-foreground sm:text-5xl">
                {t(operator.title)}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                {t(operator.description)}
              </p>
              <a
                href={enquiryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              >
                {enquiryLabel}
              </a>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                {t({
                  en: "Independent registration and documentation support. The buyer decides registration, tender invitations and contract awards. We do not represent the operator or guarantee contracts.",
                  ar: "دعم مستقل للتسجيل وإعداد الوثائق. تتخذ الجهة المشترية قرارات التسجيل والدعوة للمناقصات وإحالة العقود. لا نمثّل الشركة المشغّلة ولا نضمن الحصول على عقود.",
                })}
              </p>
            </div>
            {operator.image && (
              <div>
                <EditorialImage image={operator.image} inverse />
              </div>
            )}
          </div>
        </div>
      </section>

      <div
        className={
          editorial
            ? "mx-auto grid max-w-[1280px] gap-12 px-5 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-20 lg:px-8"
            : ""
        }
      >
        {editorial && (
          <div className="pt-12 lg:order-2 lg:py-16">
            <ArticleSidebar
              points={operator.keyPoints}
              sections={operator.sections?.map((section, index) => ({
                id: `registration-section-${index}`,
                label: section.heading,
              }))}
            />
          </div>
        )}
        <div
          className={
            editorial
              ? "min-w-0 lg:order-1 [&>section]:mx-0 [&>section]:max-w-[75ch] [&>section]:px-0 [&>section]:scroll-mt-28 [&_h2]:scroll-mt-28 [&_p]:leading-[1.85]"
              : ""
          }
        >
          {/* INTRO (BLUF) */}
          <section className="mx-auto max-w-3xl px-4 lg:px-8 py-16 text-start">
            <p className="text-lg leading-relaxed text-muted-foreground">{t(operator.intro)}</p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {t({ en: "This is part of our ", ar: "هذا جزء من خدمة " })}
              <LangLink
                to="/services/oil-gas-registration"
                className="font-semibold text-primary hover:underline"
              >
                {t({ en: "oil and gas vendor registration", ar: "تسجيل موردي النفط والغاز" })}
              </LangLink>
              {operator.isoNeeded.length > 0 ? (
                <>
                  {t({
                    en: " service. If your buyer requests ISO evidence, read our guide to ",
                    ar: " لدينا. إذا طلب المشتري أدلة متعلقة بالأيزو، فاقرأ دليلنا حول ",
                  })}
                  <LangLink
                    to="/insights/which-iso-standard-does-my-company-need"
                    className="font-semibold text-primary hover:underline"
                  >
                    {t({ en: "choosing an ISO standard", ar: "اختيار معيار الأيزو" })}
                  </LangLink>
                  {t({ en: ".", ar: "." })}
                </>
              ) : (
                t({ en: " service.", ar: " لدينا." })
              )}
            </p>
          </section>

          {/* KEY FACTS */}
          {operator.facts && operator.facts.length > 0 && (
            <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-12 text-start">
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {t(labels.factsTitle)}
              </h2>
              <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-100 bg-card shadow-soft">
                <table className="w-full text-sm">
                  <tbody>
                    {operator.facts.map((f) => (
                      <tr key={f.label.en} className="border-b border-slate-100 last:border-0">
                        <th
                          scope="row"
                          className="w-40 px-5 py-3.5 text-start align-top font-semibold text-card-foreground"
                        >
                          {t(f.label)}
                        </th>
                        <td className="px-5 py-3.5 leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                          {t(f.value)}
                          {f.sourceUrl && (
                            <a
                              href={f.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ms-2 inline-block text-primary underline underline-offset-2"
                            >
                              {t({ en: "BOC source", ar: "مصدر شركة نفط البصرة" })}
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* FACT-DENSE SECTIONS */}
          {operator.sections?.map((s, index) => (
            <section
              id={`registration-section-${index}`}
              key={s.heading.en}
              className="mx-auto max-w-3xl px-4 lg:px-8 pb-12 text-start"
            >
              <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                {t(s.heading)}
              </h2>
              {s.paragraphs.map((p) => (
                <p key={p.en} className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {t(p)}
                </p>
              ))}
              {operator.slug === "basra-oil-company" &&
                s.heading.en === "Confirm HSE and ISO evidence" && (
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    {t({
                      en: "If your buyer names an ISO requirement, our guide to ",
                      ar: "إذا حدّدت الجهة المشترية متطلباً لشهادة أيزو، يوضح دليل ",
                    })}
                    <LangLink
                      to="/insights/which-iso-standard-does-my-company-need"
                      className="font-semibold text-primary underline underline-offset-2"
                    >
                      {t({ en: "choosing an ISO standard", ar: "اختيار معيار الأيزو" })}
                    </LangLink>
                    {t({
                      en: " explains the main options; it does not replace BOC’s instructions.",
                      ar: " الخيارات الأساسية؛ ولا يحلّ محل تعليمات شركة نفط البصرة.",
                    })}
                  </p>
                )}
              {s.links && s.links.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {s.links.map((l) => (
                    <li key={l.to}>
                      {l.to === "/contact" ? (
                        <a
                          href={enquiryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-primary hover:underline"
                        >
                          {t(l.label)}
                        </a>
                      ) : (
                        <LangLink to={l.to} className="font-semibold text-primary hover:underline">
                          {t(l.label)}
                        </LangLink>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* PLATFORM CALLOUT */}
          {operator.platform && (
            <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-4">
              <div className="rounded-2xl border border-primary/20 bg-accent p-6 text-start sm:p-8">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  {t(labels.platformTitle)} — {operator.platform.name}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t(operator.platform.note)}
                </p>
              </div>
            </section>
          )}

          {/* HOW IT WORKS */}
          <section className="mx-auto max-w-3xl px-4 lg:px-8 py-12 text-start">
            <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
              {t(labels.howTitle)}
            </h2>
            <div className="mt-8 grid gap-4">
              {operator.steps.map((step, i) => (
                <div
                  key={step.title.en}
                  className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-card p-6 shadow-soft"
                >
                  <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-card-foreground">{t(step.title)}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {t(step.text)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* DOCUMENTS + ISO */}
          <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-16 text-start">
            <div className={`grid gap-8 ${operator.isoNeeded.length > 0 ? "sm:grid-cols-2" : ""}`}>
              <div>
                <div className="flex items-center gap-2.5">
                  <FileText className="size-5 text-primary" />
                  <h2 className="text-lg font-bold text-foreground">
                    {t(operator.documentsTitle ?? labels.docsTitle)}
                  </h2>
                </div>
                <ul className="mt-4 space-y-2.5">
                  {operator.documents.map((doc) => (
                    <li key={doc.en} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span className="text-sm text-muted-foreground">{t(doc)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {operator.isoNeeded.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5">
                    <ListChecks className="size-5 text-primary" />
                    <h2 className="text-lg font-bold text-foreground">{t(labels.isoTitle)}</h2>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {t(
                      operator.isoNote ?? {
                        en: "Check the buyer's current requirements for your activity before arranging certification. The standards below are reference options; their relevance depends on the requested scope and evidence.",
                        ar: "راجع متطلبات المشتري الحالية لنشاطك قبل ترتيب أعمال منح الشهادات. المعايير أدناه خيارات مرجعية؛ وتعتمد صلتها بطلبك على النطاق والأدلة المطلوبة.",
                      },
                    )}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {operator.isoNeeded.map((iso) => {
                      const path = isoServicePath(iso);
                      return path ? (
                        <LangLink
                          key={iso}
                          to={path}
                          className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                        >
                          {iso}
                        </LangLink>
                      ) : (
                        <span
                          key={iso}
                          className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                        >
                          {iso}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* SOURCES */}
          {operator.sources && operator.sources.length > 0 && (
            <section className="mx-auto max-w-3xl px-4 lg:px-8 pb-16 text-start">
              <h2 className="text-lg font-bold text-foreground">{t(labels.sourcesTitle)}</h2>
              <ol className="mt-4 list-decimal space-y-2 ps-5">
                {operator.sources.map((s) => (
                  <li key={s.url} className="text-sm text-muted-foreground">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t(s.label)}
                    </a>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>

      {/* FAQ */}
      <FaqSection items={operator.faq} className="bg-gradient-to-b from-background to-accent/40" />

      {/* CTA */}
      <section className="bg-gradient-to-b from-accent/40 to-accent">
        <div className="mx-auto max-w-3xl px-4 lg:px-8 py-20 text-center">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            {t(content.services.energyVendors.title)}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            {t({
              en: "Independent registration and documentation support; the buyer retains all approval and award decisions.",
              ar: "دعم مستقل للتسجيل وإعداد الوثائق؛ وتبقى قرارات القبول وإحالة العقود بيد الجهة المشترية.",
            })}
          </p>
          <a
            href={enquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground shadow-soft transition-all hover:brightness-110"
          >
            {enquiryLabel}
          </a>
        </div>
      </section>
    </>
  );
}
